// Data access for the dashboard: studios, projects, panoramas, and file uploads to /api.
import { MEDIA_BASE } from '@archviewvr/config';
import type { ProjectManifest } from '@archviewvr/engine';
import { select, insert, update, rpc, remove, accessToken, currentSession } from './supa';

export interface Studio { id: string; name: string; slug: string }
export interface Membership { studio_id: string; role: 'owner' | 'editor' | 'viewer'; studios: Studio }
export interface Project {
  id: string; studio_id: string; name: string; client_name: string | null; slug: string;
  state: 'trial' | 'published' | 'paused' | 'archived';
  model_key: string | null; storage_bytes: number; storage_limit: number;
  start_pano_id: string | null; start_heading?: number; start_pitch?: number; settings?: Record<string, unknown>;
  created_at: string; updated_at: string;
  published_at?: string | null; stripe_subscription_id?: string | null; free_until?: string | null;
  brand?: ProjectBrand;
  lead_enabled?: boolean; lead_required?: boolean; lead_email?: string | null;
  access_hash?: string | null;
  panoramas?: Array<{ count: number }>;
}
/** A project's brand kit. File keys are relative to the project's draft folder. */
export interface ProjectBrand {
  logo?: string;
  /** A light version of the logo for the dark theme. */
  logoDark?: string;
  /** No logo anywhere in the tour. */
  hideLogo?: boolean;
  accent?: string;
  title?: string;
  tagline?: string;
  /** Loading screen background: plain, one of the renders, an uploaded picture or a looping video. */
  background?: { kind: 'none' } | { kind: 'render'; pano: string } | { kind: 'image'; key: string } | { kind: 'video'; key: string };
  theme?: 'light' | 'dark';
  /** Panel text colour; the theme's own when empty. */
  text?: string;
  shape?: 'round' | 'soft' | 'square';
  /** Google Fonts family, or 'custom' with fontFile (an uploaded font). */
  font?: string;
  fontFile?: string;
  /** Wait for an Explore button on the loading screen, then fade slowly into the tour. */
  explore?: boolean;
  /** "Take this tour home" QR button in the tour on computers. */
  qr?: boolean;
  /** Background music loop (file key); buyers turn it on with a button. */
  music?: string;
  /** Enquire tab on the side of the tour, and its label. */
  enquireTab?: boolean;
  enquireLabel?: string;
  /** Kiosk mode timings (seconds) and whether its QR card shows. */
  kiosk?: { idle?: number; dwell?: number; qr?: boolean };
}

export const FONTS = ['Plus Jakarta Sans', 'Inter', 'Manrope', 'DM Sans', 'Montserrat', 'Outfit', 'Lora', 'Playfair Display', 'Cormorant Garamond'];
export const DEFAULT_FONT = 'Plus Jakarta Sans';
/** Loads a Google font into this page (for previews). */
export function loadGoogleFont(family: string): void {
  if (!family || family === DEFAULT_FONT || document.querySelector(`link[data-font="${family}"]`)) return;
  const l = document.createElement('link'); l.rel = 'stylesheet'; l.dataset.font = family;
  l.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family).replace(/%20/g, '+')}:wght@400;500;600;700&display=swap`;
  document.head.append(l);
}

/** The brand as the tour file (manifest) describes it. */
export function manifestBrand(b: ProjectBrand | undefined, panoKeys?: Map<string, string>): import('@archviewvr/engine').ManifestBrand {
  const out: import('@archviewvr/engine').ManifestBrand = {};
  if (!b) return out;
  if (b.logo) out.logo = b.logo;
  if (b.logoDark) out.logoDark = b.logoDark;
  if (b.hideLogo) out.hideLogo = true;
  if (b.accent) out.accent = b.accent;
  if (b.title) out.title = b.title;
  if (b.tagline) out.tagline = b.tagline;
  if (b.background?.kind === 'render') out.loadingBackground = panoKeys?.get(b.background.pano) ?? `panos/${b.background.pano}-2k.jpg`;
  if (b.background?.kind === 'image') out.loadingBackground = b.background.key;
  if (b.background?.kind === 'video') out.loadingVideo = b.background.key;
  if (b.theme === 'dark') out.theme = 'dark';
  if (b.text) out.text = b.text;
  if (b.shape && b.shape !== 'round') out.shape = b.shape;
  if (b.font === 'custom' && b.fontFile) { out.font = 'custom'; out.fontUrl = b.fontFile; }
  else if (b.font && b.font !== DEFAULT_FONT) out.font = b.font;
  if (b.explore) out.explore = true;
  if (b.qr) out.qr = true;
  if (b.music) out.music = b.music;
  if (b.enquireTab) out.enquire = { label: b.enquireLabel || 'Enquire' };
  if (b.kiosk) out.kiosk = { ...b.kiosk };
  return out;
}

// ------------------------------------------------------------------ saved styles (studio brand kits)

/** A style saved for reuse across projects. Files are full web addresses so any project can copy them. */
export interface KitStyle extends Omit<ProjectBrand, 'title' | 'background' | 'logo' | 'logoDark' | 'fontFile' | 'music'> {
  logo?: string; logoDark?: string; fontFile?: string; music?: string;
  background?: { kind: 'none' } | { kind: 'image'; url: string } | { kind: 'video'; url: string };
  lead?: { enabled: boolean; required: boolean };
}
export interface BrandKit { id: string; studio_id: string; name: string; data: KitStyle; created_at: string }

export async function getBrandKits(studioId: string): Promise<BrandKit[]> {
  const rows = await select<BrandKit>('brand_kits', `studio_id=eq.${studioId}&select=id,studio_id,name,data,created_at&order=created_at.asc`);
  return rows.filter((k) => k.data && Object.keys(k.data).length);
}
export async function saveBrandKit(studioId: string, name: string, data: KitStyle, id?: string): Promise<BrandKit> {
  if (id) return (await update<BrandKit>('brand_kits', `id=eq.${id}`, { name, data }))[0];
  return (await insert<BrandKit>('brand_kits', { studio_id: studioId, name, data }))[0];
}
export const deleteBrandKit = (id: string) => remove('brand_kits', `id=eq.${id}`);

// ------------------------------------------------------------------ enquiries

export interface LeadRow { id: string; name: string | null; email: string; phone: string | null; consent: boolean; created_at: string; message?: string | null; source?: string; room?: string | null; read_at?: string | null }
const LEAD_COLS = 'id,name,email,phone,consent,created_at,message,source,room';
/** A project's enquiries, newest first. (Before the read_at migration runs, without read marks.) */
export async function getLeads(projectId: string): Promise<LeadRow[]> {
  try { return await select<LeadRow>('leads', `project_id=eq.${projectId}&select=${LEAD_COLS},read_at&order=created_at.desc&limit=1000`); }
  catch { return select<LeadRow>('leads', `project_id=eq.${projectId}&select=${LEAD_COLS}&order=created_at.desc&limit=1000`); }
}
/** How many enquiries nobody has opened yet (0 when read marks aren't set up). */
export async function unreadLeads(projectId: string): Promise<number> {
  try { return (await select<{ id: string }>('leads', `project_id=eq.${projectId}&read_at=is.null&select=id&limit=100`)).length; }
  catch { return 0; }
}
/** Marks enquiries read (all of them with no ids), or unread again. */
export const markLeadsRead = (projectId: string, ids: string[] | null, read = true) =>
  rpc<number>('mark_leads_read', { p_project: projectId, p_ids: ids, p_read: read });

export interface PanoRow {
  id: string; project_id: string; name: string; room?: string | null; set_name: string; level_id: string | null;
  x: number; y: number; z: number; yaw: number; check_state: string;
  base_key: string; mobile_key: string | null; full_key: string | null; bytes: number; sort: number;
  /** Depth map (PNG) and its size, included in bytes. Missing before the depth maps migration. */
  depth_key?: string | null; depth_bytes?: number;
}
export interface LevelRow { id: string; project_id: string; name: string; height: number; sort: number }

export async function myStudios(): Promise<Membership[]> {
  const uid = currentSession()?.user.id;
  if (!uid) return [];
  return select<Membership>('members', `user_id=eq.${uid}&select=studio_id,role,studios(id,name,slug)`);
}

export const slugify = (s: string) =>
  s.toLowerCase().normalize('NFKD').replace(/[^\w\s-]/g, '').trim().replace(/[\s_]+/g, '-').replace(/-+/g, '-').slice(0, 40) || 'project';
const suffix = () => Math.random().toString(36).slice(2, 6);

export async function createStudio(name: string): Promise<string> {
  let slug = slugify(name);
  if (slug.length < 2) slug = `studio-${suffix()}`;
  try { return await rpc<string>('create_studio', { studio_name: name, studio_slug: slug }); }
  catch { return rpc<string>('create_studio', { studio_name: name, studio_slug: `${slug}-${suffix()}` }); }
}

export async function listProjects(studioId: string): Promise<Project[]> {
  return select<Project>('projects', `studio_id=eq.${studioId}&state=neq.archived&select=*,panoramas!panoramas_project_id_fkey(count)&panoramas.set_name=eq.default&order=updated_at.desc`);
}

export async function getProject(id: string): Promise<Project | null> {
  return (await select<Project>('projects', `id=eq.${id}&select=*`))[0] ?? null;
}

export async function createProject(studioId: string, name: string, client: string): Promise<Project> {
  const [p] = await insert<Project>('projects', { studio_id: studioId, name, client_name: client || null, slug: `${slugify(name)}-${suffix()}` });
  return p;
}

/** The main renders (the default set). */
export async function getPanoramas(projectId: string): Promise<PanoRow[]> {
  return select<PanoRow>('panoramas', `project_id=eq.${projectId}&set_name=eq.default&select=*&order=sort.asc`);
}
/** Alternate panoramas: other renders at the same spot as a main one (same name, another set). */
export async function getAlternates(projectId: string): Promise<PanoRow[]> {
  return select<PanoRow>('panoramas', `project_id=eq.${projectId}&set_name=neq.default&select=*&order=sort.asc`);
}
export async function getLevels(projectId: string): Promise<LevelRow[]> {
  return select<LevelRow>('levels', `project_id=eq.${projectId}&select=*&order=sort.asc`);
}

/** Removes a project's panoramas and levels before a fresh upload replaces them. */
/** Removes one file from the project's draft folder (best effort). */
export async function deleteFile(projectId: string, key: string): Promise<void> {
  const token = await accessToken();
  if (!token) return;
  await fetch(`/api/files/${projectId}/${key}`, { method: 'DELETE', headers: { authorization: `Bearer ${token}` } }).catch(() => {});
}

export async function clearProjectContent(projectId: string): Promise<void> {
  await update('projects', `id=eq.${projectId}`, { start_pano_id: null });
  await remove('panoramas', `project_id=eq.${projectId}`);
  await remove('levels', `project_id=eq.${projectId}`);
}

export { insert, update };

/** Reads the project's current draft manifest (the file the viewer loads). */
export async function getDraftManifest(projectId: string): Promise<ProjectManifest> {
  const res = await fetch(`${draftUrl(projectId, 'manifest.json')}?t=${Date.now()}`, { cache: 'no-store' });
  if (!res.ok) throw new Error('Couldn’t read the saved tour. Try saving the renders again.');
  return res.json();
}

/** Public URL of a file in a project's draft folder. */
export const draftUrl = (projectId: string, key: string) => `${MEDIA_BASE}/drafts/${projectId}/${key}`;

/** Uploads one file to drafts/<projectId>/<key>, reporting progress (0..1). */
export async function uploadFile(projectId: string, key: string, body: Blob, onProgress?: (f: number) => void): Promise<void> {
  const token = await accessToken();
  if (!token) throw new Error('Sign in again to upload');
  await new Promise<void>((resolve, reject) => {
    const x = new XMLHttpRequest();
    x.open('PUT', `/api/files/${projectId}/${key}`);
    x.setRequestHeader('authorization', `Bearer ${token}`);
    x.upload.onprogress = (e) => { if (e.lengthComputable) onProgress?.(e.loaded / e.total); };
    x.onload = () => {
      if (x.status >= 200 && x.status < 300) { onProgress?.(1); resolve(); return; }
      let msg = `Upload failed (${x.status})`;
      try { msg = JSON.parse(x.responseText).error || msg; } catch { /* not json */ }
      reject(new Error(msg));
    };
    x.onerror = () => reject(new Error('Upload failed: check your connection'));
    x.send(body);
  });
}

async function api<T>(path: string, body?: object): Promise<T> {
  const token = await accessToken();
  if (!token) throw new Error('Sign in again');
  const res = await fetch(`/api/${path}`, body === undefined
    ? { headers: { authorization: `Bearer ${token}` } }
    : { method: 'POST', headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' }, body: JSON.stringify(body) });
  const out = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw new Error(out.error || `Request failed (${res.status})`);
  return out;
}

/** Deletes a project that isn't published, with its renders, settings and files (owner only). */
export async function deleteProject(projectId: string): Promise<void> {
  const token = await accessToken();
  if (!token) throw new Error('Sign in again');
  const res = await fetch(`/api/projects/${projectId}`, { method: 'DELETE', headers: { authorization: `Bearer ${token}` } });
  const out = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok) throw new Error(out.error || `Request failed (${res.status})`);
}
/** Every file in a project's draft folder, with its size. */
export const listFiles = (projectId: string) => api<{ files: Array<{ key: string; size: number }> }>(`files/${projectId}`).then((r) => r.files);

/** Sends the studio to Stripe Checkout to publish a project. */
export async function startCheckout(projectId: string): Promise<void> {
  location.href = await checkoutUrl(projectId);
}
/** A Stripe Checkout page to publish a project (the project needs saved renders). */
export const checkoutUrl = (projectId: string) => api<{ url: string }>('billing/checkout', { projectId }).then((r) => r.url);
/** Opens Stripe's billing page (cards, invoices, cancelling). */
export async function openBilling(projectId: string): Promise<void> {
  const { url } = await api<{ url: string }>('billing/portal', { projectId });
  location.href = url;
}
/** Refreshes a published project's live tour after its renders are re-saved. */
export async function syncPublished(projectId: string): Promise<boolean> {
  return (await api<{ synced: boolean }>(`projects/${projectId}/sync`, {})).synced;
}

// ------------------------------------------------------------------ platform admin

/** Whether the signed-in person runs ArchviewVR (shows the settings cog). */
export async function checkAdmin(): Promise<boolean> {
  const uid = currentSession()?.user.id;
  if (!uid) return false;
  try { return (await select('platform_admins', `user_id=eq.${uid}&select=user_id`)).length > 0; }
  catch { return false; } // table not there yet
}

export interface AdminProject {
  id: string; name: string; slug: string; state: Project['state']; storage_bytes: number; storage_limit?: number; white_label?: boolean; renders: number;
  created_at: string; published_at: string | null; free_until: string | null; stripe_subscription_id: string | null;
}
export interface AdminStudio { id: string; name: string; role: string; members: number; stripe_customer_id: string | null; projects: AdminProject[] }
export interface AdminAccount {
  id: string; email: string; name: string | null; created_at: string; last_sign_in_at: string | null;
  confirmed: boolean; admin: boolean; studios: AdminStudio[];
}
export const adminOverview = () => api<AdminAccount[]>('admin/overview');
export const adminGiveFree = (projectId: string, months: number) => api<{ free_until: string }>('admin/free', { projectId, months });
export const adminCancelTour = (projectId: string) => api<{ ok: true }>('admin/cancel', { projectId });
export const adminSetWhiteLabel = (projectId: string, on: boolean) => api<{ ok: true; white_label: boolean }>('admin/white-label', { projectId, on });
export const adminSetStorage = (projectId: string, bytes: number) => api<{ ok: true; storage_limit: number }>('admin/storage', { projectId, bytes });
export const adminDeleteAccount = (userId: string) => api<{ projects: number; files: number }>('admin/delete-account', { userId });

/** Admin only: saves site content (the guided tutorial) to storage. Returns the file's public URL. */
export async function adminPutSite(key: string, body: Blob, onProgress?: (f: number) => void): Promise<string> {
  const token = await accessToken();
  if (!token) throw new Error('Sign in again');
  await new Promise<void>((resolve, reject) => {
    const x = new XMLHttpRequest();
    x.open('PUT', `/api/admin/site/${key}`);
    x.setRequestHeader('authorization', `Bearer ${token}`);
    x.upload.onprogress = (e) => { if (e.lengthComputable) onProgress?.(e.loaded / e.total); };
    x.onload = () => {
      if (x.status >= 200 && x.status < 300) { resolve(); return; }
      let msg = `Upload failed (${x.status})`;
      try { msg = JSON.parse(x.responseText).error || msg; } catch { /* not json */ }
      reject(new Error(msg));
    };
    x.onerror = () => reject(new Error('Upload failed: check your connection'));
    x.send(body);
  });
  return `${MEDIA_BASE}/site/${key}`;
}

// ------------------------------------------------------------------ analytics

export interface NamedCount { name: string; views: number; country?: string }
export interface TourStats {
  views: number; visitors: number; seconds: number; avg_seconds: number; leads: number;
  prev: { views: number; visitors: number; avg_seconds: number };
  days: Array<{ day: string; views: number; visitors: number }>;
  projects: Array<{ id: string; views: number; visitors: number; avg_seconds: number; last: string }>;
  countries: NamedCount[]; cities: NamedCount[]; devices: NamedCount[]; referrers: NamedCount[];
  actions: Record<string, number>;
  panos: Array<{ id: string; visits: number; sessions: number }>;
  recent: Array<{ id: string; project_id: string; started_at: string; seconds: number; device: string | null; country: string | null; city: string | null; referrer: string | null; panos: number }>;
}
export interface TourPlaces {
  samples: number;
  cells: Array<[number, number, number, number]>;
  looks: Array<[number, number, number, number]>;
  paths: Array<[number, number, number, number, number, number]>;
  flow: Array<[string, string, number]>;
  panos: Record<string, number>;
}

const timeZone = () => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Australia/Brisbane'; } catch { return 'Australia/Brisbane'; } };

export const tourStats = (projectIds: string[], since: Date, until: Date) =>
  rpc<TourStats>('tour_stats', { project_ids: projectIds, since: since.toISOString(), until: until.toISOString(), tz: timeZone() });
/** Alternate panoramas: time on each scheme, and the pairs people compared (needs the 2026-10-12 SQL). */
export interface TourSchemes {
  schemes: Array<{ name: string; seconds: number; views: number; sessions: number }>;
  compares: Array<{ a: string; b: string; seconds: number; times: number; sessions: number }>;
}
export const tourSchemes = (projectId: string, since: Date, until: Date) =>
  rpc<TourSchemes>('tour_schemes', { project: projectId, since: since.toISOString(), until: until.toISOString() });
export const tourPlaces = (projectId: string, since: Date, until: Date) =>
  rpc<TourPlaces>('tour_places', { project: projectId, since: since.toISOString(), until: until.toISOString() });

/** Clears a tour's visits. Enquiries are kept. Returns how many visits were removed. */
export const resetTourAnalytics = (projectId: string) => rpc<number>('reset_tour_analytics', { project: projectId });

// ------------------------------------------------------------------ hotspots

export type HotspotKind = 'info' | 'image' | 'video' | 'product' | 'link';
export interface HotspotRow {
  id: string; project_id: string; kind: HotspotKind; x: number; y: number; z: number;
  title: string; body: string | null; image_keys: string[]; video_url: string | null;
  supplier: string | null; price: string | null; link_label: string | null; link_url: string | null; lead_gate: boolean;
  /** which way the surface faces at the spot, and the leader line length in metres (0 = off) */
  nx: number | null; ny: number | null; nz: number | null; leader: number;
}
export const getHotspots = (projectId: string) =>
  select<HotspotRow>('hotspots', `project_id=eq.${projectId}&select=*&order=created_at.asc`);

/** Saves a project's hotspots as given (removed ones are deleted). */
export async function replaceHotspots(projectId: string, rows: HotspotRow[]): Promise<void> {
  await remove('hotspots', `project_id=eq.${projectId}`);
  if (rows.length) await insert('hotspots', rows.map((r, i) => ({ ...r, project_id: projectId, created_at: new Date(Date.now() + i).toISOString() })));
}

/** A hotspot as the tour file describes it. Picture keys stay relative unless url() is given (live preview). */
export function hotspotToManifest(r: HotspotRow, url: (key: string) => string = (k) => k): import('@archviewvr/engine').ManifestHotspot {
  return {
    id: r.id, kind: r.kind, position: [r.x, r.y, r.z], title: r.title || 'Untitled',
    ...(r.nx != null && r.ny != null && r.nz != null ? { normal: [r.nx, r.ny, r.nz] as [number, number, number] } : {}),
    ...(r.leader > 0 && r.nx != null ? { leader: r.leader } : {}),
    ...(r.body ? { body: r.body } : {}),
    ...(r.kind !== 'video' && r.image_keys.length ? { images: r.image_keys.map(url) } : {}),
    ...(r.kind === 'video' && r.video_url ? { video: r.video_url } : {}),
    ...(r.kind === 'product' && r.supplier ? { supplier: r.supplier } : {}),
    ...(r.kind === 'product' && r.price ? { price: r.price } : {}),
    ...((r.kind === 'product' || r.kind === 'link') && r.link_url ? { link: { label: r.link_label || '', url: r.link_url } } : {}),
    ...(r.lead_gate ? { leadGate: true } : {}),
  };
}

// ------------------------------------------------------------------ automatic room names

/** Asks the AI to name each render's room. Throws "not switched on" when the Claude key isn't set. */
export const suggestRooms = (projectId: string) => api<{ rooms: Record<string, string>; named: number }>('ai/rooms', { projectId });

const ROOM_WORDS: Array<[RegExp, string]> = [
  [/master|mbr|main.?bed/i, 'Master bedroom'], [/ensuite|ens\b/i, 'Ensuite'], [/wir|walk.?in/i, 'Walk-in robe'],
  [/bed(room)?.?(\d)/i, 'Bedroom $2'], [/bed/i, 'Bedroom'], [/kitchen|kit\b/i, 'Kitchen'], [/butler|scullery/i, 'Butler’s pantry'],
  [/living|lounge|liv\b/i, 'Living'], [/dining|din\b/i, 'Dining'], [/family|fam\b/i, 'Family'], [/rumpus/i, 'Rumpus'],
  [/media|theatre|cinema/i, 'Media room'], [/study|office/i, 'Study'], [/powder|wc\b/i, 'Powder room'], [/bath/i, 'Bathroom'],
  [/laundry|ldry/i, 'Laundry'], [/entry|foyer|front.?door/i, 'Entry'], [/hall/i, 'Hallway'], [/stair/i, 'Stairs'],
  [/alfresco|patio|outdoor/i, 'Alfresco'], [/balcony|bal\b/i, 'Balcony'], [/terrace|deck/i, 'Terrace'], [/pool/i, 'Pool'],
  [/garage/i, 'Garage'], [/lobby/i, 'Lobby'], [/roof/i, 'Rooftop'], [/gym/i, 'Gym'], [/cellar|wine/i, 'Wine cellar'],
];
/** A room name from words in a camera name (Cam_Kitchen_01 → Kitchen), or null. */
export function roomFromCameraName(name: string): string | null {
  const s = name.replace(/[_\-.]+/g, ' ');
  for (const [re, label] of ROOM_WORDS) { const m = s.match(re); if (m) return label.replace('$2', m[2] ?? ''); }
  return null;
}
